import { useLazyQuery } from "@apollo/client";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GridInputs, SearchForm } from "../../../../components/Functional";
import { Button, Spinner } from "../../../../components/ui";
import { FETCH_ARTISTS_SEARCH as fetchArtistsSearch } from "../../../../graphql/query/Artist";
import { FETCH_ARTWORK_CATEGORIES } from "../../../../graphql/query/ArtworkCategory";
import { FETCH_INSTANCES_LIST as fetchInstancesListQuery } from "../../../../graphql/query/Instance";
import { FETCH_STATUS } from "../../../../graphql/query/Status";
import { useCompaniesLazy } from "../../../../hooks/useCompanies";
import useUser from "../../../../hooks/useUser";
import { instanceFilterStore, useInstanceFilters } from "../useInstanceFilters";
import Artfairs from "./components/Artfairs";
import Auctions from "./components/Auctions";
import Condition from "./components/Condition";
import Consignments from "./components/Consignments";
import History from "./components/History";
import Jobs from "./components/Jobs";
import Location from "./components/Location";
import Provenance from "./components/Provenance";
import Query from "./components/Query";
import Shipments from "./components/Shipments";
import Sort from "./components/Sort";
import Tabs from "./components/Tabs";
import Technical from "./components/Technical";
import Warehouse from "./components/Warehouse";

const InstanceSearch = props => {
  const { closeModal, isSearchOpen = false, onSearchSubmitted } = props || {};
  const [
    fetchInstancesV2,
    { loading: instancesLoading, data: { instancesV2 } = {} },
  ] = useLazyQuery(fetchInstancesListQuery);
  const [
    fetchArtistsLazily,
    { loading: artistsLoading, data: { artists = [] } = {} },
  ] = useLazyQuery(fetchArtistsSearch());
  const [
    fetchArtworkCategoriesLazily,
    { loading: categoriesLoading, data: { artworkCategories = [] } = {} },
  ] = useLazyQuery(FETCH_ARTWORK_CATEGORIES);
  const [fetchCompanies, { loading: companiesLoading, companies }] =
    useCompaniesLazy();
  const [
    fetchStatusLazily,
    { loading: statusLoading, data: { status = [] } = {} },
  ] = useLazyQuery(FETCH_STATUS);

  const { hasPermission } = useUser();
  const readCompanyPermission = hasPermission("READ_COMPANY");
  const readArtistPermission = hasPermission("READ_ARTIST");

  useEffect(() => {
    if (readArtistPermission && isSearchOpen) {
      fetchArtistsLazily();
    }
  }, [readArtistPermission, isSearchOpen]);

  useEffect(() => {
    if (readCompanyPermission && isSearchOpen) {
      fetchCompanies();
    }
  }, [readCompanyPermission, isSearchOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      fetchArtworkCategoriesLazily();
      fetchStatusLazily();
    }
  }, [isSearchOpen]);

  const navigate = useNavigate();
  const location = useLocation();
  const { formik } = useInstanceFilters((values) => {
    const input = instanceFilterStore.get();
    delete input.previousRef;
    delete input?.checkSiteManaged;
    delete input?.checkExcludeUnlocatedShipments;
    delete input?.control;
    delete input?.temporaryRelease;

    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams({ status: "all" });
    ["listId", "imageListId", "workingListId", "imageWorkingListId"].forEach(
      key => {
        const value = currentParams.get(key);
        if (value) {
          nextParams.set(key, value);
        }
      },
    );

    fetchInstancesV2({
      variables: {
        input,
      },
    }).then(() => {
      navigate(`/instance?${nextParams.toString()}`);
      // Performing a new search invalidates any selection from the previous
      // result set; clear it so stale ids cannot reach bulk actions.
      onSearchSubmitted?.();
      closeModal();
    });
  });

  useEffect(() => {
    if (formik?.values?.fabricatedIsNull) {
      formik?.setFieldValue("fabricated", {
        operator: "isNull",
        value: null,
      });
    } else if (formik?.values?.fabricated?.operatpor === "isNull") {
      formik?.setFieldValue("fabricated", null);
    }
  }, [formik?.values?.fabricatedIsNull]);

  useEffect(() => {
    if (
      formik?.values?.fabricatedIsNull === true &&
      [-1, 0]?.includes(formik?.values?.fabricated)
    ) {
      formik?.setFieldValue("fabricatedIsNull", false);
    }
  }, [formik?.values?.fabricated]);

  useEffect(() => {
    if (formik?.values?.previousRef) {
      formik?.setFieldValue("_item.ref", {
        operator: "like",
        value: formik?.values?.previousRef,
      });
      formik?.setFieldValue("_consignmentWork.ref", {
        operator: "like",
        value: formik?.values?.previousRef,
      });
    } else if (!formik?.values?.previousRef) {
      formik?.setFieldValue("_item.ref", null);
      formik?.setFieldValue("_consignmentWork.ref", null);
    }
  }, [formik?.values?.previousRef]);

  useEffect(() => {
    if (formik?.values?.onLoan === true) {
      formik?.setFieldValue("_consignmentWork._consignment.due", {
        operator: "gt",
        value: new Date(),
      });
      formik?.setFieldValue("_consignmentWork._consignment.typeId", {
        operator: "eq",
        value: 2,
      });
    } else if (formik?.values?.onLoan === false) {
      formik?.setFieldValue("_consignmentWork._consignment.due", null);
      formik?.setFieldValue("_consignmentWork._consignment.typeId", null);
    }
  }, [formik?.values?.onLoan]);

  const standardOptions = [
    { label: "Standard", value: 0 },
    { label: "Non-Standard", value: -1 },
    { label: "UnChecked", value: null },
  ];

  const inputs = {
    className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 lg:gap-16 mt-5",
    inputs: [
      {
        className: "gap-4",
        inputs: [
          {
            isMulti: true,
            label: "Control Company",
            name: "companyId",
            type: "multi-select",
            options: companies?.edges?.map(item => ({
              label: `${item?.code ? item?.code + " |" : ""} ${item?.name || ""}`,
              value: item?.id,
            })),
          },
          { label: "Control Reference", name: "ref", type: "text" },
          {
            isMulti: true,
            label: "Artist",
            type: "multi-select",
            disabled: !readArtistPermission,
            name: "_edition._artwork._artist.id",
            options: artists?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
          {
            isMulti: true,
            label: "Node Company",
            name: "nodeId",
            type: "multi-select",
            disabled: !readCompanyPermission,
            options: companies?.edges?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
          {
            hasOperator: true,
            label: "Title",
            name: "_edition._artwork.title",
            type: "text",
          },
          { label: "Year", name: "_edition.year", type: "text" },
          {
            hasOperator: true,
            label: "Medium",
            name: "_edition._artwork.medium",
            type: "text",
          },
          { label: "Name", name: "name", type: "text" },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          { label: "Previous Reference", name: "previousRef", type: "text" },
          {
            label: "Artist Representation",
            name: "_edition._artwork._artist.represented",
            type: "multi-select",
            options: [
              { label: "Internal", value: 0 },
              { label: "External", value: 1 },
              { label: "Secondary Market", value: 2 },
              { label: "Not Represented", value: 3 },
            ],
          },
          {
            hasOperator: true,
            label: "Artist Ref",
            name: "artistRef",
            type: "text",
          },
          {
            hasOperator: true,
            isMulti: true,
            label: "Category",
            name: "_edition._artwork.categoryId",
            type: "multi-select",
            options: artworkCategories?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
          {
            hasOperator: true,
            label: "Series",
            name: "_edition._artwork.series",
            type: "text",
          },
          { label: "Edition no", name: "editionNumber", type: "number" },
          {
            hasOperator: true,
            label: "Edition Size",
            name: "_edition.number",
            type: "number",
          },
          {
            hasOperator: true,
            isMulti: true,
            label: "Status",
            name: "statusId",
            type: "multi-select",
            options: status?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            hasOperator: true,
            label: "Class",
            name: "_edition._artwork.classId",
            type: "number",
          },
          {
            label: "Dimensons",
            name: "_edition._artwork.dimensions",
            type: "text",
          },
          {
            label: "Handling",
            name: "_edition._artwork.standardId",
            type: "multi-select",
            options: standardOptions,
          },
          {
            label: "Description",
            name: "_edition._artwork.description",
            type: "text",
          },
          { label: "Notes", name: "notes", type: "text" },
          {
            label: "Certificate",
            name: "certificate",
            type: "text",
            postLabelInput: {
              designType: "threeStateCheckbox",
              label: "Sent",
              name: "certificateSent",
              type: "toggle",
            },
          },
          { label: "Id", name: "id", type: "number" },

          ...(hasPermission("READ_EXHIBITION")
            ? [
                {
                  hasOperator: true,
                  label: "Exhibition",
                  name: "_exhibitionWork._exhibition.id",
                  type: "exhibitionSearch",
                },
              ]
            : [
                {
                  hasOperator: false,
                  label: "Exhibition",
                  name: "mockExhibition",
                  type: "text",
                  disabled: true,
                  className: "opacity-50 [&_*]:!cursor-not-allowed",
                },
              ]),
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Ecommerce",
            name: "channelEcommerce",
            type: "multi-select",
            options: [
              { label: "True", value: 1 },
              { label: "False", value: 0 },
            ],
          },
          {
            label: "Presentation",
            name: "channelPresentation",
            type: "multi-select",
            options: [
              { label: "None", value: 0 },
              { label: "Public", value: 1 },
              { label: "Private", value: 2 },
            ],
          },
          {
            designType: "threeStateCheckbox",
            label: "Fabricated",
            name: "fabricated",
            type: "toggle",
            postLabelInput: {
              designType: "checkbox",
              label: "Is Null",
              name: "fabricatedIsNull",
              type: "toggle",
            },
          },
          {
            designType: "threeStateCheckbox",
            label: "For Sale",
            name: "forSale",
            type: "toggle",
          },
          {
            designType: "threeStateCheckbox",
            label: "Framed",
            name: "framed",
            type: "toggle",
          },
          {
            designType: "checkbox",
            label: "On Loan",
            name: "onLoan",
            type: "toggle",
          },
          {
            designType: "threeStateCheckbox",
            label: "Photographed",
            name: "_edition._artwork.photographed",
            type: "toggle",
          },
        ],
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  const searchButtonProps = {
    action: "black",
    label: instancesLoading ? "Searching" : "Search",
    disabled: instancesLoading,
    type: "submit",
  };

  const resetButtonProps = {
    action: "default",
    label: "Reset",
    onClick: formik?.resetForm,
  };

  const tabComponentProps = {
    instancesV2,
    formik,
  };

  const tabsProps = {
    formik,
    name: "tab",
    options: [
      {
        label: "Consignments",
        value: "consignments",
        component: <Consignments {...tabComponentProps} />,
      },
      {
        label: "Provenance",
        value: "provenance",
        component: <Provenance {...tabComponentProps} />,
      },
      {
        label: "Auctions",
        value: "auctions",
        component: <Auctions {...tabComponentProps} />,
      },
      {
        label: "History",
        value: "history",
        component: <History {...tabComponentProps} />,
      },
      {
        label: "Artfairs",
        value: "artfairs",
        component: <Artfairs {...tabComponentProps} />,
      },
      {
        label: "Location",
        value: "location",
        component: <Location {...tabComponentProps} />,
      },
      {
        label: "Shipments",
        value: "shipments",
        component: <Shipments {...tabComponentProps} />,
      },
      {
        label: "Condition",
        value: "condition",
        component: <Condition {...tabComponentProps} />,
      },
      {
        label: "Technical",
        value: "technical",
        component: <Technical {...tabComponentProps} />,
      },
      {
        label: "Jobs",
        value: "jobs",
        component: <Jobs {...tabComponentProps} />,
      },
      {
        label: "Warehouse",
        value: "warehouse",
        component: <Warehouse {...tabComponentProps} />,
      },
      {
        label: "Sort",
        value: "sort",
        component: <Sort {...tabComponentProps} />,
      },
      {
        label: "Query",
        value: "query",
        component: <Query {...tabComponentProps} />,
      },
    ],
  };

  return (
    <SearchForm onSubmit={formik.handleSubmit}>
      <div className="flex flex-col">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xl font-bold">Instance Search</div>
          <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
            <Button {...resetButtonProps} />
            <Button {...searchButtonProps} />
          </div>
        </div>
        {statusLoading ||
        artistsLoading ||
        companiesLoading ||
        categoriesLoading ? (
          <div className="flex h-[50dvh] sm:h-[60dvh] lg:h-[70dvh] w-full items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
            <GridInputs {...inputProps} />
            <Tabs {...tabsProps} />
          </div>
        )}
      </div>
    </SearchForm>
  );
};

export default InstanceSearch;
